import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { createMasterAgent } from '../services/agentApi';
import { Users, CheckCircle, ArrowLeft, Loader, Database } from 'lucide-react';

/**
 * AgentConfiguration - Page for creating a new master agent.
 *
 * Flow (Image 1):
 * 1. User enters Agent Name (required)
 * 2. Target Database is pre-filled from the current database (read-only)
 * 3. User enters Description (optional)
 * 4. Click "Create Agent" button
 */
export const AgentConfiguration = () => {
    const { t } = useTranslation(['pages-misc-1', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const databaseFromState = location.state?.database;
    const [loading, setLoading] = useState(false);
    const [currentDatabase, setCurrentDatabase] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        database_connection_id: '',
        description: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Set database from state or find from context
        const db = databaseFromState || databases.find(db => db.id === parseInt(location.pathname.split('/')[3]));
        if (db) {
            setCurrentDatabase(db);
            setFormData(prev => ({
                ...prev,
                database_connection_id: db.id.toString()
            }));
        }
    }, [databaseFromState, databases, location.pathname]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('pages-misc-1:agentConfiguration.agentNameRequired');
        }

        if (!currentDatabase) {
            newErrors.database_connection_id = t('pages-misc-1:agentConfiguration.targetDatabaseRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast(t('pages-misc-1:agentConfiguration.pleaseFillRequiredFields'), 'error');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: formData.name.trim(),
                database_connection_id: currentDatabase.id,
                description: formData.description.trim() || null
            };

            const response = await createMasterAgent(payload);

            if (response.data?.status === 'success') {
                showToast(t('pages-misc-1:agentConfiguration.agentCreatedSuccess'), 'success');
                // Navigate to agent settings page for document upload and training
                navigate(`/agents/settings/${response.data.data.id}`);
            }
        } catch (error) {
            console.error('Failed to create agent:', error);
            const errorMessage = error.response?.data?.detail || error.message || t('pages-misc-1:agentConfiguration.agentCreateFailed');
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (!currentDatabase) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={handleBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('pages-misc-1:agentConfiguration.backToAiAgents')}
                </button>

                <div className="flex items-center space-x-3">
                    <Users className="w-8 h-8 text-primary-600" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('pages-misc-1:agentConfiguration.title')}</h1>
                        <p className="text-gray-600">{t('pages-misc-1:agentConfiguration.subtitle')}</p>
                    </div>
                </div>
            </div>

            {/* Agent Configuration Form */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="mb-6 pb-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('pages-misc-1:agentConfiguration.formSectionTitle')}</h2>
                    <p className="text-sm text-gray-500">{t('pages-misc-1:agentConfiguration.formSectionSubtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Agent Name and Target Database */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Agent Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('pages-misc-1:agentConfiguration.agentName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder={t('pages-misc-1:agentConfiguration.agentNamePlaceholder')}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                                    errors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                                disabled={loading}
                            />
                            {errors.name && (
                                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                            )}
                        </div>

                        {/* Target Database (Read-only) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('pages-misc-1:agentConfiguration.targetDatabase')} <span className="text-red-500">*</span>
                            </label>
                            <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 flex items-center">
                                <Database className="w-5 h-5 text-gray-500 mr-2 flex-shrink-0" />
                                <span className="truncate">{currentDatabase.name}</span>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                                {t('pages-misc-1:agentConfiguration.targetDatabaseHint')}
                            </p>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('common:description')}
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder={t('pages-misc-1:agentConfiguration.descriptionPlaceholder')}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                            disabled={loading}
                        />
                        <p className="mt-2 text-sm text-gray-500">
                            {t('pages-misc-1:agentConfiguration.descriptionHint')}
                        </p>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-all ${
                                loading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary-600 text-white hover:bg-primary-700'
                            }`}
                        >
                            {loading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    <span>{t('common:creating')}</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    <span>{t('pages-misc-1:agentConfiguration.createAgent')}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
